import { CommonModule } from '@angular/common';
import { Apollo, gql } from 'apollo-angular';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  TemplateRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { GoogleMap, MapMarker, MapPolygon } from '@angular/google-maps';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { FormlyFieldSelectProps } from '@ngx-formly/core/select';
import { TranslateService } from '@ngx-translate/core';
import { fromLonLat } from 'ol/proj';
import { of } from 'rxjs';
import { AutoComplete } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';

type LegacyAutocompletePrediction = google.maps.places.AutocompletePrediction;
type NewAutocompleteSuggestion = {
  placePrediction?: {
    placeId?: string;
    text?: { text?: string };
    mainText?: { text?: string };
    secondaryText?: { text?: string };
  };
};
type GeocodeSuggestion = LegacyAutocompletePrediction | NewAutocompleteSuggestion;
type GeocodeAutocompleteItem = {
  completo: string;
  placeId: string;
  prediction: GeocodeSuggestion;
};

export interface GeocodeValue {
  id?: string;
  completo: string;
  manuale?: boolean | null;
  geom_?: {
    type: 'Point';
    coordinates: [number, number];
  } | null;
  toponimo?: string | null;
  civico?: string | null;
  cap?: string | null;
}

export interface GeocodeProps extends FormlyFieldProps, FormlyFieldSelectProps {
  showClear?: boolean;
  delay?: number;
  forceSelection?: boolean;
  autoHighlight?: boolean;
  appendTo?: 'body' | null;
  country?: string;
  language?: string;
  enableMapPreview?: boolean;
  enableOpenMapButton?: boolean;
  openMapButtonLabel?: string;
  closeMapButtonLabel?: string;
  confirmPositionLabel?: string;
  noticeNearBoundaryLabel?: string;
  noticeOutsideCabinaLabel?: string;
  noticeAddressNotFoundLabel?: string;
  noticeCivicNotFoundLabel?: string;
  noticePositionNotVerifiedLabel?: string;
  showPositionNotVerifiedNotice?: boolean;
  mapPreviewCodAc?: string | null;
  mapPreviewThresholdMeters?: number;
  mapTypeId?: google.maps.MapTypeId | string;
  toggleMap?: () => void;
  isMapOpen?: () => boolean;
  isMapToggleDisabled?: () => boolean;
  search: (event: unknown) => void;
}

@Component({
  selector: 'formly-field-geocode',
  standalone: true,
  imports: [
    CommonModule,
    ...FORM_IMPORTS,
    AutoComplete,
    ButtonModule,
    GoogleMap,
    MapMarker,
    MapPolygon,
  ],
  template: `
    <div class="relative w-full">
      <p-autocomplete
        #element
        [formControl]="formControl"
        [formlyAttributes]="field"
        [suggestions]="addresses()"
        optionLabel="completo"
        [placeholder]="geocodePlaceholder()"
        [delay]="props.delay ?? 300"
        [forceSelection]="props.forceSelection ?? false"
        [autoHighlight]="props.autoHighlight ?? false"
        [showClear]="props.showClear ?? false"
        [showEmptyMessage]="false"
        [appendTo]="props.appendTo ?? 'body'"
        (completeMethod)="search($event)"
        (onSelect)="onSelectAddress($event.value)"
        (onBlur)="normalizeManualInput()"
        class="w-full"
      >
        <ng-template let-item pTemplate="item">
          {{ item?.completo ?? '' }}
        </ng-template>
        <ng-template let-value pTemplate="selectedItem">
          {{ value?.completo ?? '' }}
        </ng-template>
      </p-autocomplete>

      @if (isBusy()) {
        <span
          class="absolute right-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none"
        >
          <span
            class="block h-4 w-4 rounded-full border-2 border-(--p-primary-color) border-t-transparent animate-spin bg-white/70"
          ></span>
        </span>
      }
    </div>

    <ng-template #detachedMapTpl>
      @if (showMapPreview()) {
        <div class="mt-2 flex flex-col gap-2">
          @if (mapNotice()) {
            <div class="p-message p-message-warn w-full">
              <div
                class="p-message-content min-h-12 flex flex-wrap justify-between items-center"
              >
                <div>{{ mapNotice() }}</div>
                @if (showConfirmPositionButton()) {
                  <p-button
                    type="button"
                    severity="warn"
                    size="small"
                    (click)="confirmPosition()"
                  >
                    {{ confirmPositionLabel() }}
                  </p-button>
                }
              </div>
            </div>
          }
          <div
            [class]="
              mapExpanded() ? 'fixed inset-0 z-1200 bg-white' : 'relative'
            "
          >
            <p-button
              type="button"
              styleClass="absolute right-3 top-3 z-[1201]"
              [icon]="
                mapExpanded() ? 'fa-solid fa-compress' : 'fa-solid fa-expand'
              "
              [rounded]="true"
              [severity]="$any('white')"
              (click)="toggleMapExpanded(); $event.stopPropagation()"
            />

            <google-map
              [height]="mapExpanded() ? '100dvh' : '320px'"
              [width]="mapExpanded() ? '100dvw' : '100%'"
              [zoom]="14"
              [center]="mapCenter()"
              [options]="mapOptions()"
              [class]="
                mapExpanded()
                  ? 'block'
                  : 'block rounded-(--p-card-border-radius) overflow-hidden'
              "
            >
              @if (showMainMarker()) {
                <map-marker
                  [position]="mapMarker()!"
                  [options]="mainMarkerOptions()"
                  (mapDragend)="onMainMarkerDragEnd($event)"
                ></map-marker>
              }
              @for (polygon of mapPolygons(); track $index) {
                <map-polygon
                  [paths]="polygon.paths"
                  [options]="polygon.options"
                ></map-polygon>
              }
              @for (marker of mapCodAcMarkers(); track $index) {
                <map-marker
                  [position]="marker.position"
                  [options]="marker.options"
                ></map-marker>
              }
            </google-map>
          </div>
        </div>
      }
    </ng-template>
  `,
  styles: `
    :host ::ng-deep .cabina-marker-label {
      text-shadow:
        -1px -1px 0 #000,
        1px -1px 0 #000,
        -1px 1px 0 #000,
        1px 1px 0 #000;
      letter-spacing: 0.2px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeocodeFieldType
  extends FieldType<FieldTypeConfig<GeocodeProps>>
  implements AfterViewInit, OnDestroy
{
  @ViewChild('element') element!: AutoComplete;
  @ViewChild('detachedMapTpl') detachedMapTpl!: TemplateRef<unknown>;
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  private autocompleteService?: google.maps.places.AutocompleteService;
  private autocompleteSuggestionClass?: {
    fetchAutocompleteSuggestions: (
      request: Record<string, unknown>,
    ) => Promise<{ suggestions?: unknown[] }>;
  };
  private autocompleteSessionToken?: unknown;
  private geocoder?: google.maps.Geocoder;
  private placesLibraryLoading?: Promise<void>;
  private lastKnownId?: string;
  private predictionsRequestId = 0;
  addresses = signal<GeocodeAutocompleteItem[]>([]);
  private readonly _apollo = inject(Apollo);
  private boundaryRequestId = 0;
  private readonly CABINE_BY_RADIUS_QUERY = gql`
    query GetCabineByPointRadius($point: geometry!, $distance: Float!) {
      energy_village_area(
        where: { geom: { _st_d_within: { from: $point, distance: $distance } } }
      ) {
        id
        geom
        cabina {
          cod_ac
        }
      }
    }
  `;
  private readonly CABINA_BY_CODAC_QUERY = gql`
    query GetCabinaByCodAc($codAc: String!) {
      energy_village_area(
        where: { cabina: { cod_ac: { _eq: $codAc } } }
        limit: 1
      ) {
        id
        geom
        cabina {
          cod_ac
        }
      }
    }
  `;
  private controlValue = signal<GeocodeValue | string | null>(null);
  private typedByUser = signal(false);
  private selectedSuggestion = signal(false);
  private manualNoResult = signal(false);
  private keepMapVisible = signal(false);
  private mapDismissedByConfirm = signal(false);
  private forcedMapOpen = signal(false);
  mapExpanded = signal(false);
  private resolvingSelection = signal(false);
  private boundaryCheckPending = signal(false);
  private predictionsPending = signal(false);
  private markerDragPending = signal(false);
  private queryEditing = signal(false);
  private holdMapHiddenUntilBoundaryResolved = signal(false);
  private selectionTransitionPending = signal(false);
  private boundaryOverlay = signal<{
    forceMap: boolean;
    polygons: Array<{
      paths: google.maps.LatLngLiteral[];
      options: google.maps.PolygonOptions;
    }>;
    markers: Array<{
      position: { lat: number; lng: number };
      options: google.maps.MarkerOptions;
    }>;
  }>({ forceMap: false, polygons: [], markers: [] });
  private baseCabina = signal<{
    id: number;
    geom: any;
    codAc: string;
    center: { lat: number; lng: number } | null;
  } | null>(null);
  private markerCabina = signal<{
    id: number;
    geom: any;
    codAc: string;
  } | null>(null);
  private baseCabinaRequestId = 0;
  private readonly defaultCenter = { lat: 41.9028, lng: 12.4964 };
  private browserLocation = signal<{ lat: number; lng: number } | null>(null);
  private manualMarkerPosition = signal<{ lat: number; lng: number } | null>(
    null,
  );
  mapCenter = computed(() => {
    const coords = this.currentCoordinates(this.controlValue());
    if (coords) return { lat: coords[1], lng: coords[0] };
    if (this.manualMarkerPosition()) return this.manualMarkerPosition()!;
    if (this.baseCabina()?.center) return this.baseCabina()!.center!;
    if (this.browserLocation()) return this.browserLocation()!;
    return this.defaultCenter;
  });
  mapMarker = computed(() => {
    if (this.manualMarkerPosition()) return this.manualMarkerPosition();
    const coords = this.currentCoordinates(this.controlValue());
    if (!coords && this.baseCabina()?.center) return this.baseCabina()!.center!;
    if (!coords && this.browserLocation()) return this.browserLocation()!;
    if (!coords) return this.defaultCenter;
    return { lat: coords[1], lng: coords[0] };
  });
  showMainMarker = computed(() => this.showMapPreview());
  mainMarkerOptions = computed<google.maps.MarkerOptions>(() => ({
    draggable: !this.formControl.disabled,
  }));
  mapOptions = computed<google.maps.MapOptions>(() => ({
    mapTypeId:
      (this.props.mapTypeId as google.maps.MapTypeId | string) ?? 'hybrid',
    fullscreenControl: false,
    ...(this.formControl.disabled
      ? {
          gestureHandling: 'none' as const,
          zoomControl: false,
          keyboardShortcuts: false,
          disableDoubleClickZoom: true,
          clickableIcons: false,
        }
      : {}),
  }));
  mapPolygons = computed(() => {
    let current = this.boundaryOverlay().polygons;
    const hasRequiredCabina =
      String(this.props.mapPreviewCodAc ?? '').trim().length > 0;
    type CabinaWithGeom = {
      id: number;
      geom: any;
      codAc: string;
      center?: { lat: number; lng: number } | null;
    };
    const cabineCandidates: Array<CabinaWithGeom | null> = hasRequiredCabina
      ? [this.baseCabina()]
      : [this.markerCabina() ?? this.baseCabina()];
    const cabineToShow = cabineCandidates.filter(
      (c): c is CabinaWithGeom => !!c?.geom,
    );
    const cabinaStyle: google.maps.PolygonOptions = hasRequiredCabina
      ? {
          strokeColor: '#FFB000',
          strokeOpacity: 1,
          strokeWeight: 3,
          fillColor: '#FFD54F',
          fillOpacity: 0.2,
          zIndex: 3000,
        }
      : {
          strokeColor: '#FFB000',
          strokeOpacity: 1,
          strokeWeight: 3,
          fillColor: '#FFD54F',
          fillOpacity: 0.2,
          zIndex: 3000,
        };

    for (const cabina of cabineToShow) {
      const rings = this.extractOuterRings(cabina.geom);
      for (const paths of rings) {
        if (!paths || paths.length < 3) continue;
        const existingIndex = current.findIndex((p) =>
          this.samePaths(p.paths, paths),
        );
        if (existingIndex >= 0) {
          const updated = [...current];
          updated[existingIndex] = {
            ...updated[existingIndex],
            options: {
              ...(updated[existingIndex]?.options ?? {}),
              ...cabinaStyle,
            } as google.maps.PolygonOptions,
          };
          current = updated;
          continue;
        }
        current = [
          ...current,
          {
            paths,
            options: cabinaStyle,
          },
        ];
      }
    }

    return current;
  });
  mapCodAcMarkers = computed(() => {
    const current = this.boundaryOverlay().markers;
    const base = this.baseCabina();
    if (!base?.codAc || !base?.geom) return current;

    const hasBaseLabel = current.some((m) => {
      const label = m.options?.label;
      const labelText = typeof label === 'string' ? label : (label?.text ?? '');
      return labelText === base.codAc;
    });
    if (hasBaseLabel) return current;

    const anchor = this.mapMarker() ?? this.mapCenter();
    const basePaths = this.extractFirstOuterRing(base.geom, anchor);
    if (!basePaths || basePaths.length < 3) return current;
    const position = this.labelPointInCabinaNearAnchor(basePaths, anchor);

    return [
      ...current,
      {
        position,
        options: {
          label: {
            text: base.codAc,
            className: 'cabina-marker-label',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '700',
            fontFamily: 'Arial, sans-serif',
          },
          icon: this.getLabelOnlyIcon(),
          clickable: false,
          zIndex: 1100,
        } as google.maps.MarkerOptions,
      },
    ];
  });
  geocodePlaceholder = computed(() => {
    const raw = this.props.placeholder;
    if (typeof raw === 'string' && raw.trim().length > 0) return raw;
    return this.translate.instant('formly.geocode.placeholder');
  });
  confirmPositionLabel = computed(
    () =>
      this.props.confirmPositionLabel ??
      this.translate.instant('formly.geocode.confirm_position'),
  );
  isBusy = computed(
    () =>
      this.predictionsPending() ||
      this.resolvingSelection() ||
      this.boundaryCheckPending() ||
      this.markerDragPending(),
  );
  showMapPreview = computed(() => {
    if (!this.props.enableMapPreview) return false;
    if (this.queryEditing()) return false;
    if (this.holdMapHiddenUntilBoundaryResolved()) return false;
    if (this.selectionTransitionPending()) return false;
    if (this.forcedMapOpen()) return true;
    if (this.resolvingSelection()) return false;
    if (this.selectedSuggestion() && this.boundaryCheckPending()) return false;
    if (this.mapDismissedByConfirm()) return false;
    const value = this.controlValue();
    const hasAddress =
      typeof value === 'string'
        ? value.trim().length > 0
        : !!(value as GeocodeValue | null)?.completo;
    if (!hasAddress) return false;
    if (!this.typedByUser()) return false;
    if (this.isOutsideSelectedCabina()) return true;
    const persistedMapOpened =
      !!value &&
      typeof value === 'object' &&
      ((value as GeocodeValue).manuale === true ||
        (value as any).modificato_manualmente === true ||
        (value as any).map_opened === true);
    if (persistedMapOpened) return true;
    const missingCivico =
      !!value &&
      typeof value === 'object' &&
      String((value as GeocodeValue).civico ?? '').trim().length === 0;
    if (missingCivico) return true;
    if (this.manualNoResult()) return true;
    if (this.keepMapVisible()) return true;
    if (this.selectedSuggestion() && this.boundaryOverlay().forceMap)
      return true;
    return false;
  });
  mapNotice = computed(() => {
    if (!this.showMapPreview()) return null;
    const value = this.controlValue();
    const hasGeom = !!this.currentCoordinates(value);
    const outsideSelectedCabina = this.isOutsideSelectedCabina();
    const missingCivico =
      !!value &&
      typeof value === 'object' &&
      String((value as GeocodeValue).civico ?? '').trim().length === 0;
    const positionNotVerified =
      this.props.showPositionNotVerifiedNotice === true &&
      !!value &&
      typeof value === 'object' &&
      (value as GeocodeValue).manuale === true;

    // Priorita' messaggi:
    // 1) confine (sempre)
    // 2) fuori cabina selezionata
    // 3) indirizzo non trovato
    // 4) civico mancante
    if (this.boundaryOverlay().forceMap) {
      return (
        this.props.noticeNearBoundaryLabel ??
        this.translate.instant('formly.geocode.notice.near_boundary')
      );
    }
    if (outsideSelectedCabina) {
      return (
        this.props.noticeOutsideCabinaLabel ??
        this.translate.instant('formly.geocode.notice.outside_cabina')
      );
    }
    if (this.manualNoResult() || !hasGeom) {
      return (
        this.props.noticeAddressNotFoundLabel ??
        this.translate.instant('formly.geocode.notice.address_not_found')
      );
    }
    if (positionNotVerified) {
      return (
        this.props.noticePositionNotVerifiedLabel ??
        this.translate.instant('formly.geocode.notice.position_not_verified')
      );
    }
    if (missingCivico) {
      return (
        this.props.noticeCivicNotFoundLabel ??
        this.translate.instant('formly.geocode.notice.civic_not_found')
      );
    }
    return null;
  });
  showConfirmPositionButton = computed(() => {
    if (!this.showMapPreview()) return false;
    const marker = this.mapMarker();
    const baseGeom = this.baseCabina()?.geom;
    if (!baseGeom) return true;
    if (!marker) return false;
    return this.geomContainsPoint(baseGeom, marker.lng, marker.lat);
  });

  private isOutsideSelectedCabina(): boolean {
    const coords = this.currentCoordinates(this.controlValue());
    const baseGeom = this.baseCabina()?.geom;
    if (!coords || !baseGeom) return false;
    return !this.geomContainsPoint(baseGeom, coords[0], coords[1]);
  }

  ngOnInit() {
    this.props.openMapButtonLabel ??= this.translate.instant(
      'formly.geocode.button.apri',
    );
    this.props.closeMapButtonLabel ??= this.translate.instant(
      'formly.geocode.button.chiudi',
    );
    this.props['search'] = this.search.bind(this);
    this.props.toggleMap = this.onMapToggleClick.bind(this);
    this.props.isMapOpen = () => this.showMapPreview();
    this.props.isMapToggleDisabled = this.isMapToggleDisabled.bind(this);
    if (this.props.enableOpenMapButton && !(this.props as any).addonRight) {
      (this.props as any).addonRightDetached = true;
      (this.props as any).addonRight = {
        text: () =>
          this.showMapPreview()
            ? (this.props.closeMapButtonLabel ??
              this.translate.instant('formly.geocode.button.chiudi'))
            : (this.props.openMapButtonLabel ??
              this.translate.instant('formly.geocode.button.apri')),
        disabled: () => this.isMapToggleDisabled(),
        onClick: () => this.onMapToggleClick(),
      };
    }
    this.sanitizeControlValue();
    this.controlValue.set(
      (this.formControl.value as GeocodeValue | string | null) ?? null,
    );
    if (
      this.formControl.value &&
      typeof this.formControl.value === 'object' &&
      ((this.formControl.value as GeocodeValue).manuale === true ||
        (this.formControl.value as any).modificato_manualmente === true ||
        (this.formControl.value as any).map_opened === true)
    ) {
      this.keepMapVisible.set(true);
      this.typedByUser.set(true);
    }
    this.updateBaseCabina();
    this.loadBrowserLocationFallback();
    this.captureKnownId(this.formControl.value);
    this.debugLog('ngOnInit:ready');
    this.formControl.valueChanges.subscribe((value) => {
      this.sanitizeControlValue();
      const normalizedValue =
        (this.formControl.value as GeocodeValue | string | null) ?? null;
      this.debugLog('valueChanges:start', {
        emittedType:
          value === null || value === undefined ? 'empty' : typeof value,
        normalizedType:
          normalizedValue === null || normalizedValue === undefined
            ? 'empty'
            : typeof normalizedValue,
        sameReference: value === normalizedValue,
      });
      const isAutocompletePending =
        !!normalizedValue &&
        typeof normalizedValue === 'object' &&
        this.isAutocompletePendingValue(normalizedValue);
      if (isAutocompletePending) {
        if (this.selectedSuggestion() || this.resolvingSelection()) {
          this.holdMapHiddenUntilBoundaryResolved.set(true);
        }
        this.queryEditing.set(true);
        this.forcedMapOpen.set(false);
        this.keepMapVisible.set(false);
        this.manualNoResult.set(false);
        this.mapDismissedByConfirm.set(false);
        this.manualMarkerPosition.set(null);
        this.controlValue.set(normalizedValue);
        this.boundaryOverlay.set({ forceMap: false, polygons: [], markers: [] });
        this.captureKnownId(normalizedValue);
        this.debugLog('valueChanges:autocomplete-pending');
        return;
      }
      const isTransientAddressObject =
        !!normalizedValue &&
        typeof normalizedValue === 'object' &&
        this.isTransientAddressObject(normalizedValue);
      if (isTransientAddressObject) {
        this.queryEditing.set(true);
        this.holdMapHiddenUntilBoundaryResolved.set(true);
        this.selectionTransitionPending.set(true);
        this.forcedMapOpen.set(false);
        this.keepMapVisible.set(false);
        this.manualNoResult.set(false);
        this.mapDismissedByConfirm.set(false);
        this.manualMarkerPosition.set(null);
        this.controlValue.set(normalizedValue);
        this.boundaryOverlay.set({ forceMap: false, polygons: [], markers: [] });
        this.captureKnownId(normalizedValue);
        this.debugLog('valueChanges:transient-address-object');
        return;
      }
      const isTypingQuery =
        typeof normalizedValue === 'string' && normalizedValue.trim().length > 0;
      if (isTypingQuery) {
        this.queryEditing.set(true);
        if (!this.selectedSuggestion() && !this.resolvingSelection()) {
          this.holdMapHiddenUntilBoundaryResolved.set(false);
        }
        this.selectionTransitionPending.set(false);
        this.forcedMapOpen.set(false);
        this.keepMapVisible.set(false);
        this.manualNoResult.set(false);
        this.mapDismissedByConfirm.set(false);
        this.manualMarkerPosition.set(null);
        this.controlValue.set(normalizedValue);
        this.boundaryOverlay.set({ forceMap: false, polygons: [], markers: [] });
        this.captureKnownId(normalizedValue);
        this.debugLog('valueChanges:typing-query');
        return;
      }
      const selectedCoords = this.currentCoordinates(normalizedValue);
      const shouldHoldHiddenForSelection =
        this.selectedSuggestion() &&
        this.resolvingSelection() &&
        !!selectedCoords;
      this.holdMapHiddenUntilBoundaryResolved.set(shouldHoldHiddenForSelection);
      this.queryEditing.set(false);
      this.mapDismissedByConfirm.set(false);
      this.manualMarkerPosition.set(null);
      this.controlValue.set(normalizedValue);
      if (
        !normalizedValue ||
        (typeof normalizedValue === 'string' &&
          normalizedValue.trim().length === 0)
      ) {
        this.keepMapVisible.set(false);
      }
      this.captureKnownId(normalizedValue);
      this.debugLog('valueChanges:apply-preview');
      this.updateBoundaryPreview(normalizedValue);
    });
    this.updateBoundaryPreview(this.formControl.value);
  }

  private sanitizeControlValue() {
    const currentValue = this.formControl.value as unknown;
    if (!currentValue || typeof currentValue !== 'object') return;

    const completo = String((currentValue as any).completo ?? '').trim();
    const coords = this.currentCoordinates(
      currentValue as GeocodeValue | string | null,
    );
    const toponimo = String((currentValue as any).toponimo ?? '').trim();
    const civico = String((currentValue as any).civico ?? '').trim();
    const cap = String((currentValue as any).cap ?? '').trim();

    const hasUsefulData =
      completo.length > 0 ||
      !!coords ||
      toponimo.length > 0 ||
      civico.length > 0 ||
      cap.length > 0;

    if (!hasUsefulData) {
      this.formControl.setValue(null, { emitEvent: false });
    }
  }

  private loadBrowserLocationFallback() {
    if (this.browserLocation()) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = Number(position.coords.latitude);
        const lng = Number(position.coords.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        this.ngZone.run(() => this.browserLocation.set({ lat, lng }));
      },
      () => {
        // no-op: fallback remains defaultCenter
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }

  ngAfterViewInit() {
    if (!this.detachedMapTpl) return;
    (this.props as any).detachedTemplate = this.detachedMapTpl;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.setBodyScrollLocked(false);
  }

  async search(event: unknown) {
    if (this.resolvingSelection()) {
      this.debugLog('search:skip-resolving');
      return;
    }
    this.selectionTransitionPending.set(false);
    const query = this.extractQuery(event);
    this.debugLog('search:start', { query });
    const editingQuery = this.isEditingNewSearch(query);
    const shouldRestartPreview =
      editingQuery ||
      query.length === 0 ||
      (query.length > 0 && this.showMapPreview());
    if (shouldRestartPreview) {
      this.queryEditing.set(true);
      this.prepareForFreshSearch();
      this.debugLog('search:restart-preview');
    } else {
      this.queryEditing.set(false);
    }
    if (query.length > 0) {
      this.typedByUser.set(true);
      this.selectedSuggestion.set(false);
      this.manualNoResult.set(false);
    }

    if (!query) {
      this.predictionsPending.set(false);
      this.addresses.set([]);
      this.debugLog('search:empty-query');
      return;
    }

    const parsedCoords = this.parseCoordinatesQuery(query);
    if (parsedCoords) {
      this.predictionsPending.set(false);
      this.addresses.set([]);
      this.debugLog('search:parsed-coordinates');
      this.applyCoordinatesInput(query, parsedCoords);
      return;
    }

    await this.ensurePlacesLibraryLoaded();

    if (!this.ensureGooglePlacesServices()) {
      this.predictionsPending.set(false);
      this.addresses.set([]);
      return;
    }

    const requestId = ++this.predictionsRequestId;
    this.predictionsPending.set(true);
    try {
      const mapped = await this.fetchAutocompleteItems(query);
      if (requestId !== this.predictionsRequestId) return;
      this.predictionsPending.set(false);
      if (!mapped.length) {
        this.addresses.set([]);
        this.debugLog('search:no-predictions-empty');
        this.applyManualNoResult(query);
        return;
      }
      this.addresses.set(mapped);
      this.debugLog('search:predictions-ready', {
        count: mapped.length,
        provider: 'legacy',
      });
    } catch (error) {
      if (requestId !== this.predictionsRequestId) return;
      this.predictionsPending.set(false);
      this.addresses.set([]);
      this.debugLog('search:no-predictions-status', {
        status: 'ERROR',
        error:
          error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : null,
      });
      this.applyManualNoResult(query);
    }
  }

  onSelectAddress(item: GeocodeAutocompleteItem) {
    this.queryEditing.set(true);
    this.holdMapHiddenUntilBoundaryResolved.set(true);
    this.selectionTransitionPending.set(true);
    this.selectedSuggestion.set(true);
    this.manualNoResult.set(false);
    this.resolvingSelection.set(true);
    this.debugLog('selectAddress:start', {
      placeId: item?.placeId ?? null,
      description: item?.completo ?? null,
    });
    this.resolveSelectedAddress(item?.prediction, this.lastKnownId);
  }

  private async resolveSelectedAddress(
    prediction: GeocodeSuggestion,
    preservedId?: string,
  ) {
    const placeId = this.extractPredictionPlaceId(prediction);
    this.debugLog('resolveSelectedAddress:start', {
      placeId,
    });
    if (!placeId) {
      this.resolvingSelection.set(false);
      this.holdMapHiddenUntilBoundaryResolved.set(false);
      this.selectionTransitionPending.set(false);
      this.autocompleteSessionToken = undefined;
      this.debugLog('resolveSelectedAddress:missing-place-id');
      return;
    }

    await this.ensurePlacesLibraryLoaded();

    if (!this.ensureGooglePlacesServices() || !this.ensureGeocoder()) {
      this.resolvingSelection.set(false);
      this.holdMapHiddenUntilBoundaryResolved.set(false);
      this.selectionTransitionPending.set(false);
      this.autocompleteSessionToken = undefined;
      this.debugLog('resolveSelectedAddress:services-unavailable');
      return;
    }

    this.geocoder!.geocode(
      {
        placeId,
        language: this.props.language ?? 'it',
      },
      (results, status) => {
        this.ngZone.run(() => {
          const result = results?.[0];
          if (status !== google.maps.GeocoderStatus.OK || !result) {
            this.resolvingSelection.set(false);
            this.holdMapHiddenUntilBoundaryResolved.set(false);
            this.selectionTransitionPending.set(false);
            this.autocompleteSessionToken = undefined;
            this.debugLog('resolveSelectedAddress:geocode-failed', {
              status: status as unknown as string,
            });
            return;
          }

          const location = result.geometry?.location;
          const addressComponents = result.address_components ?? [];
          const currentId =
            this.formControl.value &&
            typeof this.formControl.value === 'object' &&
            'id' in this.formControl.value
              ? ((this.formControl.value as { id?: string }).id ?? undefined)
              : undefined;
          const toponimo = this.getAddressComponent(addressComponents, 'route');
          const civico = this.getAddressComponent(
            addressComponents,
            'street_number',
          );
          const cap = this.getAddressComponent(
            addressComponents,
            'postal_code',
          );
          const value: GeocodeValue = {
            id: preservedId ?? currentId,
            completo:
              result.formatted_address ??
              this.extractPredictionDescription(prediction) ??
              '',
            manuale:
              this.keepMapVisible() ||
              ((this.formControl.value as GeocodeValue | null)?.manuale ??
                false),
            geom_: location
              ? {
                  type: 'Point',
                  coordinates: [location.lng(), location.lat()],
                }
              : null,
            toponimo: toponimo ?? null,
            civico: civico ?? null,
            cap: cap ?? null,
          };

          this.formControl.setValue(value);
          this.captureKnownId(value);
          this.resolvingSelection.set(false);
          this.autocompleteSessionToken = undefined;
          this.debugLog('resolveSelectedAddress:success', {
            hasGeom: !!value.geom_,
            civico: value.civico ?? null,
          });
        });
      },
    );
  }

  private async ensurePlacesLibraryLoaded(): Promise<void> {
    if ((globalThis as any)?.google?.maps?.places) {
      return;
    }

    if (!this.placesLibraryLoading) {
      const importLibrary = (globalThis as any)?.google?.maps?.importLibrary;
      if (typeof importLibrary !== 'function') {
        return;
      }

      this.placesLibraryLoading = importLibrary('places').then(() => undefined);
    }

    await this.placesLibraryLoading;
  }

  private ensureGooglePlacesServices(): boolean {
    const places = (globalThis as any)?.google?.maps?.places as
      | typeof google.maps.places
      | undefined;
    if (!places) {
      return false;
    }
    const suggestionClass = (places as any)?.AutocompleteSuggestion;
    if (
      suggestionClass &&
      typeof suggestionClass.fetchAutocompleteSuggestions === 'function'
    ) {
      this.autocompleteSuggestionClass = suggestionClass as {
        fetchAutocompleteSuggestions: (
          request: Record<string, unknown>,
        ) => Promise<{ suggestions?: unknown[] }>;
      };
    } else {
      this.autocompleteSuggestionClass = undefined;
    }

    return true;
  }

  private async fetchAutocompleteItems(
    query: string,
  ): Promise<GeocodeAutocompleteItem[]> {
    return this.fetchAutocompleteItemsLegacy(query);
  }

  private async fetchAutocompleteItemsNew(
    query: string,
  ): Promise<GeocodeAutocompleteItem[]> {
    if (!this.autocompleteSuggestionClass) return [];
    const request: Record<string, unknown> = {
      input: query,
      language: this.props.language ?? 'it',
    };
    const countries = this.getAutocompleteRegionCodes();
    if (countries.length > 0) {
      request['includedRegionCodes'] = countries;
      request['region'] = countries[0];
    }
    const sessionToken = this.getAutocompleteSessionToken();
    if (sessionToken) {
      request['sessionToken'] = sessionToken;
    }

    const response =
      await this.autocompleteSuggestionClass.fetchAutocompleteSuggestions(
        request,
      );
    const suggestions = Array.isArray(response?.suggestions)
      ? response.suggestions
      : [];
    return suggestions
      .map((suggestion) => this.mapNewSuggestionToItem(suggestion))
      .filter((item): item is GeocodeAutocompleteItem => !!item);
  }

  private fetchAutocompleteItemsLegacy(
    query: string,
  ): Promise<GeocodeAutocompleteItem[]> {
    return new Promise((resolve) => {
      const places = (globalThis as any)?.google?.maps?.places as
        | typeof google.maps.places
        | undefined;
      if (!places) {
        resolve([]);
        return;
      }
      if (!this.autocompleteService) {
        this.autocompleteService = new places.AutocompleteService();
      }

      this.autocompleteService.getPlacePredictions(
        {
          input: query,
          componentRestrictions: {
            country: this.getAutocompleteRegionCodes()[0] ?? 'it',
          },
        },
        (predictions, status) => {
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          ) {
            resolve([]);
            return;
          }
          const mapped = predictions
            .map((prediction) => this.mapLegacyPredictionToItem(prediction))
            .filter((item): item is GeocodeAutocompleteItem => !!item);
          resolve(mapped);
        },
      );
    });
  }

  private mapLegacyPredictionToItem(
    prediction: LegacyAutocompletePrediction,
  ): GeocodeAutocompleteItem | null {
    if (!prediction?.place_id) return null;
    return {
      completo: prediction.description ?? '',
      placeId: prediction.place_id,
      prediction,
    };
  }

  private mapNewSuggestionToItem(
    suggestion: unknown,
  ): GeocodeAutocompleteItem | null {
    const placePrediction =
      suggestion &&
      typeof suggestion === 'object' &&
      'placePrediction' in (suggestion as Record<string, unknown>)
        ? (suggestion as Record<string, unknown>)['placePrediction']
        : null;
    if (!placePrediction || typeof placePrediction !== 'object') return null;

    const placeId = String(
      (placePrediction as Record<string, unknown>)['placeId'] ?? '',
    ).trim();
    if (!placeId) return null;

    const textObj =
      (placePrediction as Record<string, unknown>)['text'] as
        | Record<string, unknown>
        | undefined;
    const mainObj =
      (placePrediction as Record<string, unknown>)['mainText'] as
        | Record<string, unknown>
        | undefined;
    const secondaryObj =
      (placePrediction as Record<string, unknown>)['secondaryText'] as
        | Record<string, unknown>
        | undefined;

    const fullText = String(textObj?.['text'] ?? '').trim();
    const mainText = String(mainObj?.['text'] ?? '').trim();
    const secondaryText = String(secondaryObj?.['text'] ?? '').trim();
    const fallbackText = [mainText, secondaryText].filter(Boolean).join(', ');
    const completo = fullText || fallbackText || placeId;

    return {
      completo,
      placeId,
      prediction: suggestion as NewAutocompleteSuggestion,
    };
  }

  private getAutocompleteRegionCodes(): string[] {
    const raw = String(this.props.country ?? 'it').trim();
    if (!raw) return ['it'];
    const list = raw
      .split(/[,\s;]+/)
      .map((code) => code.trim().toLowerCase())
      .filter((code) => /^[a-z]{2}$/.test(code));
    return list.length ? list : ['it'];
  }

  private getAutocompleteSessionToken(): unknown {
    if (this.autocompleteSessionToken) return this.autocompleteSessionToken;
    const tokenCtor = (globalThis as any)?.google?.maps?.places
      ?.AutocompleteSessionToken;
    if (typeof tokenCtor !== 'function') return null;
    this.autocompleteSessionToken = new tokenCtor();
    return this.autocompleteSessionToken;
  }

  private extractPredictionPlaceId(
    prediction: GeocodeSuggestion | null | undefined,
  ): string | null {
    if (!prediction || typeof prediction !== 'object') return null;
    const record = prediction as Record<string, unknown>;
    const legacyId = String(record['place_id'] ?? '').trim();
    if (legacyId) return legacyId;
    const placePrediction = record['placePrediction'];
    if (!placePrediction || typeof placePrediction !== 'object') return null;
    const newId = String(
      (placePrediction as Record<string, unknown>)['placeId'] ?? '',
    ).trim();
    return newId || null;
  }

  private extractPredictionDescription(
    prediction: GeocodeSuggestion | null | undefined,
  ): string | null {
    if (!prediction || typeof prediction !== 'object') return null;
    const record = prediction as Record<string, unknown>;
    const legacyDescription = String(record['description'] ?? '').trim();
    if (legacyDescription) return legacyDescription;
    const placePrediction = record['placePrediction'];
    if (!placePrediction || typeof placePrediction !== 'object') return null;
    const text = (
      (placePrediction as Record<string, unknown>)['text'] as
        | Record<string, unknown>
        | undefined
    )?.['text'];
    const normalized = String(text ?? '').trim();
    return normalized || null;
  }

  private ensureGeocoder(): boolean {
    const maps = (globalThis as any)?.google?.maps as
      | typeof google.maps
      | undefined;
    if (!maps) return false;
    if (!this.geocoder) {
      this.geocoder = new maps.Geocoder();
    }
    return true;
  }

  private extractQuery(event: unknown): string {
    if (typeof event === 'string') return event.trim();

    if (
      !!event &&
      typeof event === 'object' &&
      'query' in event &&
      typeof (event as { query?: unknown }).query === 'string'
    ) {
      return (event as { query: string }).query.trim();
    }

    return '';
  }

  private parseCoordinatesQuery(
    query: string,
  ): { lat: number; lng: number } | null {
    const text = query.trim();
    if (!text) return null;

    // Accept only coordinate-like input to avoid treating plain addresses as points.
    if (!/^[+\-\d.,;\s]+$/.test(text)) return null;

    const parts = text.match(/[+\-]?\d{1,3}(?:[.,]\d+)?/g);
    if (!parts || parts.length !== 2) return null;

    const first = Number(parts[0].replace(',', '.'));
    const second = Number(parts[1].replace(',', '.'));
    if (!Number.isFinite(first) || !Number.isFinite(second)) return null;

    const firstIsLat = Math.abs(first) <= 90;
    const secondIsLat = Math.abs(second) <= 90;
    const firstIsLng = Math.abs(first) <= 180;
    const secondIsLng = Math.abs(second) <= 180;

    if (firstIsLat && secondIsLng) return { lat: first, lng: second };
    if (firstIsLng && secondIsLat) return { lat: second, lng: first };
    return null;
  }

  private applyCoordinatesInput(
    rawText: string,
    coords: { lat: number; lng: number },
  ) {
    this.queryEditing.set(false);
    const current = this.formControl.value;
    const currentObj =
      current && typeof current === 'object' ? (current as GeocodeValue) : null;
    const fallbackLabel = rawText.trim();

    this.typedByUser.set(true);
    this.selectedSuggestion.set(false);
    this.manualNoResult.set(false);
    this.keepMapVisible.set(true);

    const nextValue: GeocodeValue = {
      id: currentObj?.id ?? this.lastKnownId,
      completo: fallbackLabel,
      manuale: true,
      geom_: {
        type: 'Point',
        coordinates: [coords.lng, coords.lat],
      },
      toponimo: currentObj?.toponimo ?? null,
      civico: currentObj?.civico ?? null,
      cap: currentObj?.cap ?? null,
    };

    this.formControl.setValue(nextValue);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();

    if (!this.ensureGeocoder()) return;
    this.geocoder!.geocode(
      {
        location: { lat: coords.lat, lng: coords.lng },
        language: this.props.language ?? 'it',
      },
      (results, status) => {
        this.ngZone.run(() => {
          const result = results?.[0];
          if (status !== google.maps.GeocoderStatus.OK || !result) return;

          const addressComponents = result.address_components ?? [];
          const toponimo = this.getAddressComponent(addressComponents, 'route');
          const civico = this.getAddressComponent(
            addressComponents,
            'street_number',
          );
          const cap = this.getAddressComponent(addressComponents, 'postal_code');
          const currentValue =
            this.formControl.value && typeof this.formControl.value === 'object'
              ? (this.formControl.value as GeocodeValue)
              : null;

          const resolved: GeocodeValue = {
            id: currentValue?.id ?? this.lastKnownId,
            completo: result.formatted_address ?? fallbackLabel,
            manuale: true,
            geom_: {
              type: 'Point',
              coordinates: [coords.lng, coords.lat],
            },
            toponimo: toponimo ?? null,
            civico: civico ?? null,
            cap: cap ?? null,
          };

          this.formControl.setValue(resolved);
          this.captureKnownId(resolved);
        });
      },
    );
  }

  private getAddressComponent(
    components: google.maps.GeocoderAddressComponent[],
    type: string,
  ): string | undefined {
    return components.find((c) => c.types?.includes(type))?.long_name;
  }

  private applyManualNoResult(query: string) {
    this.queryEditing.set(false);
    const text = query.trim();
    if (!text) return;
    const parsedCoords = this.parseCoordinatesQuery(text);
    if (parsedCoords) {
      this.applyCoordinatesInput(text, parsedCoords);
      return;
    }

    this.typedByUser.set(true);
    this.selectedSuggestion.set(false);
    this.manualNoResult.set(true);
    this.keepMapVisible.set(true);

    const current = this.formControl.value;
    const currentObj =
      current && typeof current === 'object' ? (current as GeocodeValue) : null;

    const nextValue: GeocodeValue = {
      id: currentObj?.id ?? this.lastKnownId,
      completo: text,
      manuale: true,
      geom_: null,
      toponimo: currentObj?.toponimo ?? null,
      civico: currentObj?.civico ?? null,
      cap: currentObj?.cap ?? null,
    };

    this.formControl.setValue(nextValue);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  normalizeManualInput() {
    this.queryEditing.set(false);
    const currentValue = this.formControl.value;

    if (typeof currentValue === 'string') {
      const text = currentValue.trim();
      if (!text) return;
      const parsedCoords = this.parseCoordinatesQuery(text);
      if (parsedCoords) {
        this.applyCoordinatesInput(text, parsedCoords);
        return;
      }
      this.typedByUser.set(true);
      this.selectedSuggestion.set(false);
      this.manualNoResult.set(true);
      this.formControl.setValue({
        id: this.lastKnownId,
        completo: text,
        manuale: this.keepMapVisible(),
        geom_: null,
        toponimo: null,
        civico: null,
        cap: null,
      } as GeocodeValue);
      return;
    }

    if (
      currentValue &&
      typeof currentValue === 'object' &&
      'completo' in currentValue
    ) {
      const completo = String((currentValue as any).completo ?? '').trim();
      if (!completo) {
        const coords = this.currentCoordinates(currentValue as GeocodeValue);
        const toponimo = String((currentValue as any).toponimo ?? '').trim();
        const civico = String((currentValue as any).civico ?? '').trim();
        const cap = String((currentValue as any).cap ?? '').trim();
        if (!coords && !toponimo && !civico && !cap) {
          this.formControl.setValue(null);
        }
        return;
      }
      if (!(currentValue as any).geom_) {
        this.manualNoResult.set(true);
      }

      const normalizedValue: GeocodeValue = {
        id: (currentValue as any).id ?? undefined,
        completo,
        manuale:
          (currentValue as any).manuale ??
          (currentValue as any).modificato_manualmente ??
          (currentValue as any).map_opened ??
          this.keepMapVisible() ??
          false,
        geom_: (currentValue as any).geom_ ?? null,
        toponimo: (currentValue as any).toponimo ?? null,
        civico: (currentValue as any).civico ?? null,
        cap: (currentValue as any).cap ?? null,
      } as GeocodeValue;

      if (
        this.sameGeocodeValue(
          currentValue as GeocodeValue,
          normalizedValue as GeocodeValue,
        )
      ) {
        this.captureKnownId(currentValue);
        return;
      }

      this.formControl.setValue(normalizedValue, { emitEvent: false });
      this.captureKnownId(normalizedValue);
    }
  }

  private sameGeocodeValue(a: GeocodeValue, b: GeocodeValue): boolean {
    const aCoords = a?.geom_?.coordinates ?? null;
    const bCoords = b?.geom_?.coordinates ?? null;
    const sameCoords =
      aCoords === null
        ? bCoords === null
        : bCoords !== null &&
          Number(aCoords[0]) === Number(bCoords[0]) &&
          Number(aCoords[1]) === Number(bCoords[1]);

    return (
      String(a?.id ?? '') === String(b?.id ?? '') &&
      String(a?.completo ?? '') === String(b?.completo ?? '') &&
      !!a?.manuale === !!b?.manuale &&
      sameCoords &&
      String(a?.toponimo ?? '') === String(b?.toponimo ?? '') &&
      String(a?.civico ?? '') === String(b?.civico ?? '') &&
      String(a?.cap ?? '') === String(b?.cap ?? '')
    );
  }

  private captureKnownId(value: unknown) {
    if (!value || typeof value !== 'object') return;
    const id = (value as { id?: unknown }).id;
    if (typeof id === 'string' && id.trim().length > 0) {
      this.lastKnownId = id;
    }
  }

  private currentCoordinates(
    value: GeocodeValue | string | null,
  ): [number, number] | null {
    if (!value || typeof value === 'string') return null;
    const coords = value?.geom_?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return null;
    const lng = Number(coords[0]);
    const lat = Number(coords[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    return [lng, lat];
  }

  private updateBoundaryPreview(value: unknown) {
    this.debugLog('boundaryPreview:start');
    this.updateBaseCabina();
    if (!this.props.enableMapPreview) {
      this.holdMapHiddenUntilBoundaryResolved.set(false);
      this.selectionTransitionPending.set(false);
      this.boundaryOverlay.set({ forceMap: false, polygons: [], markers: [] });
      this.debugLog('boundaryPreview:disabled');
      return;
    }
    const coords = (value as GeocodeValue | null)?.geom_?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) {
      this.markerDragPending.set(false);
      this.holdMapHiddenUntilBoundaryResolved.set(false);
      this.selectionTransitionPending.set(false);
      this.boundaryOverlay.set({ forceMap: false, polygons: [], markers: [] });
      this.debugLog('boundaryPreview:missing-coords');
      return;
    }

    const lng = Number(coords[0]);
    const lat = Number(coords[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      this.markerDragPending.set(false);
      this.holdMapHiddenUntilBoundaryResolved.set(false);
      this.selectionTransitionPending.set(false);
      this.boundaryOverlay.set({ forceMap: false, polygons: [], markers: [] });
      this.debugLog('boundaryPreview:invalid-coords', { lng, lat });
      return;
    }

    const requestId = ++this.boundaryRequestId;
    const thresholdMeters = this.props.mapPreviewThresholdMeters ?? 50;
    this.boundaryCheckPending.set(true);
    // PostGIS ST_DWithin on geometry(4326) expects degrees, not meters.
    const thresholdDegrees = thresholdMeters / 111320;
    const point = { type: 'Point', coordinates: [lng, lat] } as any;
    this._apollo
      .query<any>({
        query: this.CABINE_BY_RADIUS_QUERY,
        variables: { point, distance: thresholdDegrees },
        fetchPolicy: 'network-only',
        errorPolicy: 'ignore',
      })
      .subscribe({
        next: (res) => {
          if (requestId !== this.boundaryRequestId) return;
          const areas = (res?.data?.energy_village_area ?? []).filter(
            (a: any) => !!a?.id,
          );
          this.renderBoundaryPreviewFromAreas(
            areas,
            this.props.mapPreviewCodAc ?? null,
            lng,
            lat,
            thresholdMeters,
            requestId,
          );
          this.boundaryCheckPending.set(false);
          this.markerDragPending.set(false);
          this.holdMapHiddenUntilBoundaryResolved.set(false);
          this.selectionTransitionPending.set(false);
          this.debugLog('boundaryPreview:success', { areasCount: areas.length });
        },
        error: () => {
          this.boundaryCheckPending.set(false);
          this.markerDragPending.set(false);
          this.holdMapHiddenUntilBoundaryResolved.set(false);
          this.selectionTransitionPending.set(false);
          this.boundaryOverlay.set({
            forceMap: false,
            polygons: [],
            markers: [],
          });
          this.debugLog('boundaryPreview:error');
        },
      });
  }

  private renderBoundaryPreviewFromAreas(
    areas: any[],
    codAcRaw: string | null,
    lng: number,
    lat: number,
    threshold: number,
    requestId: number,
  ) {
    const codAc = String(codAcRaw ?? '').trim();
    if (areas.length === 0) {
      this.markerCabina.set(null);
      this.boundaryOverlay.set({ forceMap: false, polygons: [], markers: [] });
      return;
    }
    const areaFromCodAc = codAc
      ? areas.find((a: any) => a?.cabina?.cod_ac === codAc)
      : null;
    const containingAreas = areas.filter((a: any) =>
      this.geomContainsPoint(a?.geom, lng, lat),
    );
    const markerArea = containingAreas[0] ?? null;
    if (markerArea?.id && markerArea?.geom) {
      this.markerCabina.set({
        id: Number(markerArea.id),
        geom: markerArea.geom,
        codAc: markerArea?.cabina?.cod_ac ?? '-',
      });
    } else {
      this.markerCabina.set(null);
    }
    const selectedArea = areaFromCodAc ?? containingAreas[0] ?? null;
    if (!codAc) {
      if (selectedArea?.id && selectedArea?.geom) {
        const ring = this.extractFirstOuterRing(selectedArea.geom, { lat, lng });
        this.baseCabina.set({
          id: Number(selectedArea.id),
          geom: selectedArea.geom,
          codAc: selectedArea?.cabina?.cod_ac ?? '-',
          center: ring && ring.length >= 3 ? this.centroid(ring) : null,
        });
      } else {
        this.baseCabina.set(null);
      }
    }
    const selectedGeom = selectedArea?.geom ?? null;
    const distance = this.minDistanceToBoundaryMeters([lng, lat], selectedGeom);
    const nearBoundary = distance !== null && distance <= threshold;

    const nearBoundaryWithOverlap = nearBoundary || containingAreas.length > 1;
    const polys = areas
      .map((g: any) => {
        const cod = g?.cabina?.cod_ac ?? '-';
        if (codAc && cod === codAc) return null;
        const rings = this.extractOuterRings(g.geom);
        if (!rings.length) return null;
        const labelPaths =
          this.extractFirstOuterRing(g.geom, { lat, lng }) ?? rings[0];
        if (!labelPaths || labelPaths.length < 3) return null;
        return {
          polygons: rings.map((paths) => ({
            paths,
            options: {
              strokeColor: '#00AEEF',
              strokeOpacity: 1,
              strokeWeight: 3,
              fillColor: '#4FC3F7',
              fillOpacity: 0.18,
              zIndex: 1500,
            } as google.maps.PolygonOptions,
          })),
          marker: {
            position: this.labelPointInCabinaNearAnchor(labelPaths, {
              lat,
              lng,
            }),
            options: {
              label: {
                text: cod,
                className: 'cabina-marker-label',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '700',
                fontFamily: 'Arial, sans-serif',
              },
              icon: this.getLabelOnlyIcon(),
              clickable: false,
              zIndex: 1000,
            } as google.maps.MarkerOptions,
          },
        };
      })
      .filter((x: any): x is { polygons: any[]; marker: any } => !!x);
    this.boundaryOverlay.set({
      forceMap: nearBoundaryWithOverlap,
      polygons: nearBoundaryWithOverlap
        ? polys.flatMap((p: any) => p.polygons)
        : [],
      markers: nearBoundaryWithOverlap ? polys.map((p: any) => p.marker) : [],
    });
    if (nearBoundaryWithOverlap) {
      this.keepMapVisible.set(true);
    }
  }

  private minDistanceToBoundaryMeters(
    pointLonLat: [number, number],
    geom: any,
  ): number | null {
    const rings = this.extractAllRings(geom);
    if (!rings.length) return null;
    const point = fromLonLat(pointLonLat);
    let min = Number.POSITIVE_INFINITY;
    for (const ring of rings) {
      if (ring.length < 2) continue;
      for (let i = 0; i < ring.length; i++) {
        const aLonLat = ring[i];
        const bLonLat = ring[(i + 1) % ring.length];
        const a = fromLonLat([aLonLat.lng, aLonLat.lat]);
        const b = fromLonLat([bLonLat.lng, bLonLat.lat]);
        min = Math.min(min, this.pointToSegmentDistance(point, a, b));
      }
    }
    return Number.isFinite(min) ? min : null;
  }

  private pointToSegmentDistance(
    p: number[],
    a: number[],
    b: number[],
  ): number {
    const vx = b[0] - a[0];
    const vy = b[1] - a[1];
    const wx = p[0] - a[0];
    const wy = p[1] - a[1];
    const c1 = vx * wx + vy * wy;
    if (c1 <= 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
    const c2 = vx * vx + vy * vy;
    if (c2 <= c1) return Math.hypot(p[0] - b[0], p[1] - b[1]);
    const t = c1 / c2;
    const projX = a[0] + t * vx;
    const projY = a[1] + t * vy;
    return Math.hypot(p[0] - projX, p[1] - projY);
  }

  private getLabelOnlyIcon(): google.maps.Icon | google.maps.Symbol {
    const symbolPath = (globalThis as any)?.google?.maps?.SymbolPath?.CIRCLE;
    if (symbolPath !== undefined) {
      return {
        path: symbolPath,
        scale: 0,
        fillOpacity: 0,
        strokeOpacity: 0,
      } as google.maps.Symbol;
    }

    // Fallback sicuro quando SymbolPath non e' ancora disponibile.
    return {
      url: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==',
    } as google.maps.Icon;
  }

  private extractFirstOuterRing(
    geom: any,
    anchor?: { lat: number; lng: number } | null,
  ): google.maps.LatLngLiteral[] | null {
    const rings = this.extractOuterRings(geom);
    if (!rings.length) return null;
    if (!anchor) return rings[0];

    const containingRing = rings.find((ring) => this.pointInRing(anchor, ring));
    if (containingRing) return containingRing;

    let closest = rings[0];
    let bestD2 = Number.POSITIVE_INFINITY;
    for (const ring of rings) {
      const center = this.centroid(ring);
      const d2 =
        (center.lat - anchor.lat) * (center.lat - anchor.lat) +
        (center.lng - anchor.lng) * (center.lng - anchor.lng);
      if (d2 < bestD2) {
        bestD2 = d2;
        closest = ring;
      }
    }
    return closest;
  }

  private extractOuterRings(geom: any): google.maps.LatLngLiteral[][] {
    if (!geom || !geom.type || !Array.isArray(geom.coordinates)) return [];
    const normalizeRing = (ring: any[]): google.maps.LatLngLiteral[] =>
      (ring ?? [])
        .map((coord: any[]) => ({
          lng: Number(coord?.[0]),
          lat: Number(coord?.[1]),
        }))
        .filter((p: any) => Number.isFinite(p.lng) && Number.isFinite(p.lat));

    if (geom.type === 'Polygon') {
      const outer = normalizeRing(geom.coordinates?.[0] ?? []);
      return outer.length >= 3 ? [outer] : [];
    }
    if (geom.type === 'MultiPolygon') {
      return (geom.coordinates ?? [])
        .map((polygon: any[]) => normalizeRing(polygon?.[0] ?? []))
        .filter((ring: any[]) => ring.length >= 3);
    }
    return [];
  }

  private extractAllRings(geom: any): google.maps.LatLngLiteral[][] {
    if (!geom || !geom.type || !Array.isArray(geom.coordinates)) return [];
    if (geom.type === 'Polygon') {
      return (geom.coordinates ?? [])
        .map((ring: any[]) =>
          (ring ?? [])
            .map((coord: any[]) => ({
              lng: Number(coord?.[0]),
              lat: Number(coord?.[1]),
            }))
            .filter(
              (p: any) => Number.isFinite(p.lng) && Number.isFinite(p.lat),
            ),
        )
        .filter((ring: any[]) => ring.length >= 3);
    }
    if (geom.type === 'MultiPolygon') {
      return (geom.coordinates ?? [])
        .flatMap((polygon: any[]) =>
          (polygon ?? []).map((ring: any[]) =>
            (ring ?? [])
              .map((coord: any[]) => ({
                lng: Number(coord?.[0]),
                lat: Number(coord?.[1]),
              }))
              .filter(
                (p: any) => Number.isFinite(p.lng) && Number.isFinite(p.lat),
              ),
          ),
        )
        .filter((ring: any[]) => ring.length >= 3);
    }
    return [];
  }

  private centroid(paths: google.maps.LatLngLiteral[]): {
    lat: number;
    lng: number;
  } {
    const sum = paths.reduce(
      (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
      { lat: 0, lng: 0 },
    );
    return { lat: sum.lat / paths.length, lng: sum.lng / paths.length };
  }

  private labelPointInCabinaNearAnchor(
    paths: google.maps.LatLngLiteral[],
    anchor: { lat: number; lng: number },
  ): { lat: number; lng: number } {
    const arcMidPoint = this.arcMidpointInCabina(anchor, paths, 200);
    if (arcMidPoint) return arcMidPoint;

    const center = this.centroid(paths);
    if (!this.pointInRing(center, paths)) return center;

    const targetMeters = 100;
    const meterPerDegLat = 111_320;
    const meterPerDegLng =
      111_320 * Math.max(0.2, Math.cos((anchor.lat * Math.PI) / 180));
    const dLat = center.lat - anchor.lat;
    const dLng = center.lng - anchor.lng;
    const distDeg = Math.hypot(dLat, dLng);
    const distMeters = Math.hypot(dLat * meterPerDegLat, dLng * meterPerDegLng);

    // Se marker e centro quasi coincidenti, fallback al centro.
    if (!Number.isFinite(distMeters) || distMeters < 0.5 || distDeg < 1e-9) {
      return center;
    }

    // Punto target a ~100m dal marker verso il centro cabina.
    const ratioToTarget = Math.min(0.95, targetMeters / distMeters);
    const candidateAt50m = {
      lat: anchor.lat + dLat * ratioToTarget,
      lng: anchor.lng + dLng * ratioToTarget,
    };

    const candidates: Array<{ lat: number; lng: number }> = [];

    if (this.pointInRing(anchor, paths)) {
      if (this.pointInRing(candidateAt50m, paths)) {
        candidates.push(candidateAt50m);
      }
      const t = 0.12;
      candidates.push({
        lat: anchor.lat + (center.lat - anchor.lat) * t,
        lng: anchor.lng + (center.lng - anchor.lng) * t,
      });
    }

    // Marker fuori cabina: trova il primo punto interno lungo il segmento
    // anchor -> center (il piu' vicino possibile al marker ma dentro cabina).
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      const candidate = {
        lat: anchor.lat + (center.lat - anchor.lat) * mid,
        lng: anchor.lng + (center.lng - anchor.lng) * mid,
      };
      if (this.pointInRing(candidate, paths)) {
        hi = mid;
      } else {
        lo = mid;
      }
    }

    const insideNearBoundary = {
      lat: anchor.lat + (center.lat - anchor.lat) * hi,
      lng: anchor.lng + (center.lng - anchor.lng) * hi,
    };

    // Punto di bordo piu' vicino al marker: robusto quando ci sono molte cabine.
    const nearestBoundary = this.closestPointOnRingToPoint(anchor, paths);
    if (nearestBoundary) {
      const nudgedInside = this.moveTowardsMeters(nearestBoundary, center, 20);
      if (this.pointInRing(nudgedInside, paths)) {
        candidates.push(nudgedInside);
      }
    }

    // Se il punto a ~100m risulta interno, preferiscilo per omogeneita' visiva.
    if (this.pointInRing(candidateAt50m, paths)) {
      candidates.push(candidateAt50m);
    }

    // Margine minimo verso l'interno per robustezza visiva/geometrica.
    const fallback = this.moveTowardsMeters(insideNearBoundary, center, 12);
    if (this.pointInRing(fallback, paths)) {
      candidates.push(fallback);
    }

    // Scegli sempre il candidato interno piu' vicino al marker.
    const best = this.pickClosestToAnchor(anchor, candidates);
    return best ?? center;
  }

  private arcMidpointInCabina(
    anchor: { lat: number; lng: number },
    ring: google.maps.LatLngLiteral[],
    radiusMeters: number,
  ): { lat: number; lng: number } | null {
    const meterPerDegLat = 111_320;
    const meterPerDegLng =
      111_320 * Math.max(0.2, Math.cos((anchor.lat * Math.PI) / 180));
    const rLat = radiusMeters / meterPerDegLat;
    const rLng = radiusMeters / meterPerDegLng;
    const steps = 144; // 2.5 deg

    const points = Array.from({ length: steps }, (_, i) => {
      const t = (2 * Math.PI * i) / steps;
      return {
        lat: anchor.lat + rLat * Math.sin(t),
        lng: anchor.lng + rLng * Math.cos(t),
      };
    });
    const inside = points.map((p) => this.pointInRing(p, ring));
    if (!inside.some(Boolean)) return null;

    let bestStart = -1;
    let bestLen = 0;
    for (let start = 0; start < steps; start++) {
      if (!inside[start]) continue;
      let len = 0;
      while (len < steps && inside[(start + len) % steps]) len++;
      if (len > bestLen) {
        bestLen = len;
        bestStart = start;
      }
    }
    if (bestStart < 0 || bestLen <= 0) return null;

    const arcPoints: Array<{ lat: number; lng: number }> = [];
    for (let i = 0; i <= bestLen; i++) {
      arcPoints.push(points[(bestStart + i) % steps]);
    }

    // Settore: anchor + arco + anchor.
    const sector = [anchor, ...arcPoints, anchor];
    const center = this.polygonCentroid(sector);
    if (center && this.pointInRing(center, ring)) return center;

    const midIndex = (bestStart + Math.floor(bestLen / 2)) % steps;
    return points[midIndex];
  }

  private polygonCentroid(
    polygon: Array<{ lat: number; lng: number }>,
  ): { lat: number; lng: number } | null {
    if (!polygon || polygon.length < 4) return null;
    let area2 = 0;
    let cx = 0;
    let cy = 0;
    for (let i = 0; i < polygon.length - 1; i++) {
      const x0 = polygon[i].lng;
      const y0 = polygon[i].lat;
      const x1 = polygon[i + 1].lng;
      const y1 = polygon[i + 1].lat;
      const cross = x0 * y1 - x1 * y0;
      area2 += cross;
      cx += (x0 + x1) * cross;
      cy += (y0 + y1) * cross;
    }
    if (Math.abs(area2) < 1e-14) return null;
    return { lat: cy / (3 * area2), lng: cx / (3 * area2) };
  }

  private closestPointOnRingToPoint(
    p: { lat: number; lng: number },
    ring: google.maps.LatLngLiteral[],
  ): { lat: number; lng: number } | null {
    if (!ring || ring.length < 2) return null;
    let best: { lat: number; lng: number } | null = null;
    let bestD2 = Number.POSITIVE_INFINITY;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      const q = this.closestPointOnSegment(p, a, b);
      const d2 = (q.lat - p.lat) ** 2 + (q.lng - p.lng) ** 2;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = q;
      }
    }
    return best;
  }

  private closestPointOnSegment(
    p: { lat: number; lng: number },
    a: { lat: number; lng: number },
    b: { lat: number; lng: number },
  ): { lat: number; lng: number } {
    const vx = b.lng - a.lng;
    const vy = b.lat - a.lat;
    const wx = p.lng - a.lng;
    const wy = p.lat - a.lat;
    const c2 = vx * vx + vy * vy;
    if (c2 <= 1e-18) return { lat: a.lat, lng: a.lng };
    let t = (wx * vx + wy * vy) / c2;
    t = Math.max(0, Math.min(1, t));
    return {
      lat: a.lat + vy * t,
      lng: a.lng + vx * t,
    };
  }

  private moveTowardsMeters(
    from: { lat: number; lng: number },
    to: { lat: number; lng: number },
    meters: number,
  ): { lat: number; lng: number } {
    const dLat = to.lat - from.lat;
    const dLng = to.lng - from.lng;
    const meterPerDegLat = 111_320;
    const meterPerDegLng =
      111_320 * Math.max(0.2, Math.cos((from.lat * Math.PI) / 180));
    const distMeters = Math.hypot(dLat * meterPerDegLat, dLng * meterPerDegLng);
    if (!Number.isFinite(distMeters) || distMeters <= 1e-6) return from;
    const ratio = Math.min(1, meters / distMeters);
    return {
      lat: from.lat + dLat * ratio,
      lng: from.lng + dLng * ratio,
    };
  }

  private pickClosestToAnchor(
    anchor: { lat: number; lng: number },
    candidates: Array<{ lat: number; lng: number }>,
  ): { lat: number; lng: number } | null {
    if (!candidates.length) return null;
    let best = candidates[0];
    let bestD2 =
      (best.lat - anchor.lat) * (best.lat - anchor.lat) +
      (best.lng - anchor.lng) * (best.lng - anchor.lng);
    for (let i = 1; i < candidates.length; i++) {
      const c = candidates[i];
      const d2 =
        (c.lat - anchor.lat) * (c.lat - anchor.lat) +
        (c.lng - anchor.lng) * (c.lng - anchor.lng);
      if (d2 < bestD2) {
        bestD2 = d2;
        best = c;
      }
    }
    return best;
  }

  private updateBaseCabina() {
    const codAc = String(this.props.mapPreviewCodAc ?? '').trim();
    if (!codAc) {
      this.baseCabina.set(null);
      return;
    }

    const requestId = ++this.baseCabinaRequestId;
    this._apollo
      .query<any>({
        query: this.CABINA_BY_CODAC_QUERY,
        variables: { codAc },
        fetchPolicy: 'cache-first',
        errorPolicy: 'ignore',
      })
      .subscribe({
        next: (res) => {
          if (requestId !== this.baseCabinaRequestId) return;
          const row = res?.data?.energy_village_area?.[0];
          if (!row?.id || !row?.geom) {
            this.baseCabina.set(null);
            return;
          }
          const ring = this.extractFirstOuterRing(row.geom);
          this.baseCabina.set({
            id: row.id as number,
            geom: row.geom,
            codAc: row?.cabina?.cod_ac ?? codAc,
            center: ring && ring.length >= 3 ? this.centroid(ring) : null,
          });
        },
        error: () => this.baseCabina.set(null),
      });
  }

  private samePaths(
    a: google.maps.LatLngLiteral[],
    b: google.maps.LatLngLiteral[],
  ): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (Math.abs(a[i].lat - b[i].lat) > 1e-8) return false;
      if (Math.abs(a[i].lng - b[i].lng) > 1e-8) return false;
    }
    return true;
  }

  private geomContainsPoint(geom: any, lng: number, lat: number): boolean {
    const rings = this.extractAllRings(geom);
    if (!rings.length) return false;
    return rings.some((ring) => this.pointInRing({ lng, lat }, ring));
  }

  private pointInRing(
    point: { lng: number; lat: number },
    ring: google.maps.LatLngLiteral[],
  ): boolean {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i].lng;
      const yi = ring[i].lat;
      const xj = ring[j].lng;
      const yj = ring[j].lat;

      const intersects =
        yi > point.lat !== yj > point.lat &&
        point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi || 1e-12) + xi;

      if (intersects) inside = !inside;
    }
    return inside;
  }

  onMainMarkerDragEnd(event: google.maps.MapMouseEvent) {
    this.queryEditing.set(false);
    if (this.formControl.disabled) return;
    const latLng = event?.latLng;
    if (!latLng) return;
    const lat = latLng.lat();
    const lng = latLng.lng();
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    this.manualMarkerPosition.set({ lat, lng });
    this.markerDragPending.set(true);
    this.selectedSuggestion.set(false);
    this.mapDismissedByConfirm.set(false);
    this.keepMapVisible.set(true);
    this.typedByUser.set(true);

    const current = this.formControl.value;
    const currentObj =
      current && typeof current === 'object' ? (current as GeocodeValue) : null;
    const completo =
      typeof current === 'string'
        ? current.trim()
        : String(currentObj?.completo ?? '').trim();

    const nextValue: GeocodeValue = {
      id: currentObj?.id,
      completo: completo || '',
      manuale: true,
      geom_: {
        type: 'Point',
        coordinates: [lng, lat],
      },
      toponimo: currentObj?.toponimo ?? null,
      civico: currentObj?.civico ?? null,
      cap: currentObj?.cap ?? null,
    };

    this.formControl.setValue(nextValue);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.formControl.updateValueAndValidity();
  }

  confirmPosition() {
    this.mapDismissedByConfirm.set(true);
    this.forcedMapOpen.set(false);
  }

  private openMapFromButton() {
    this.forcedMapOpen.set(true);
    this.mapDismissedByConfirm.set(false);
    this.keepMapVisible.set(true);
    this.typedByUser.set(true);
  }

  private closeMapFromButton() {
    this.forcedMapOpen.set(false);
    this.mapDismissedByConfirm.set(true);
  }

  private toggleMapFromButton() {
    if (this.showMapPreview()) {
      this.closeMapFromButton();
      return;
    }
    this.openMapFromButton();
  }

  private hasAddressForToggle(): boolean {
    const value = this.controlValue();
    if (!value) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return String((value as GeocodeValue).completo ?? '').trim().length > 0;
  }

  private isEditingNewSearch(query: string): boolean {
    const text = query.trim();
    if (!text) return true;
    const currentValue = this.formControl.value;
    const currentText =
      typeof currentValue === 'string'
        ? currentValue.trim()
        : String((currentValue as GeocodeValue | null)?.completo ?? '').trim();
    if (!currentText) return false;
    return text.toLowerCase() !== currentText.toLowerCase();
  }

  private isAutocompletePendingValue(value: unknown): boolean {
    if (!value || typeof value !== 'object') return false;
    const v = value as Record<string, unknown>;
    const hasPlaceId =
      typeof v['placeId'] === 'string' && v['placeId'].trim().length > 0;
    const hasPrediction =
      !!v['prediction'] &&
      typeof v['prediction'] === 'object' &&
      !!this.extractPredictionPlaceId(v['prediction'] as GeocodeSuggestion);
    return hasPlaceId || hasPrediction;
  }

  private isTransientAddressObject(value: unknown): boolean {
    if (!value || typeof value !== 'object') return false;
    const v = value as Record<string, unknown>;

    const completo = String(v['completo'] ?? '').trim();
    if (!completo) return false;

    const hasGeom = !!this.currentCoordinates(value as GeocodeValue);
    if (hasGeom) return false;

    const toponimo = String(v['toponimo'] ?? '').trim();
    const civico = String(v['civico'] ?? '').trim();
    const cap = String(v['cap'] ?? '').trim();
    const hasAddressPieces =
      toponimo.length > 0 || civico.length > 0 || cap.length > 0;

    const hasPlaceId =
      typeof v['placeId'] === 'string' && v['placeId'].trim().length > 0;
    const hasPrediction =
      !!v['prediction'] && typeof v['prediction'] === 'object';

    return !hasAddressPieces && !hasPlaceId && !hasPrediction;
  }

  private prepareForFreshSearch() {
    this.autocompleteSessionToken = undefined;
    this.holdMapHiddenUntilBoundaryResolved.set(false);
    this.selectionTransitionPending.set(false);
    this.forcedMapOpen.set(false);
    this.keepMapVisible.set(false);
    this.manualNoResult.set(false);
    this.mapDismissedByConfirm.set(false);
    this.markerDragPending.set(false);
    this.manualMarkerPosition.set(null);
    this.boundaryOverlay.set({ forceMap: false, polygons: [], markers: [] });
    this.debugLog('prepareForFreshSearch');
  }

  private isDebugEnabled(): boolean {
    const globalFlag =
      (globalThis as any)?.__GEOCODE_DEBUG__ ??
      (globalThis as any)?.window?.__GEOCODE_DEBUG__;
    if (globalFlag === false) return false;
    if (globalFlag === true) return true;
    try {
      if (typeof localStorage === 'undefined') return false;
      const stored = localStorage.getItem('geocode.debug');
      if (stored === '0') return false;
      if (stored === '1') return true;
      return false;
    } catch {
      return false;
    }
  }

  private debugSnapshot(): Record<string, unknown> {
    const value = this.formControl.value as GeocodeValue | string | null;
    const valueObj =
      value && typeof value === 'object'
        ? (value as unknown as Record<string, unknown>)
        : null;
    const valueType =
      value === null || value === undefined
        ? 'empty'
        : typeof value === 'string'
          ? 'string'
          : 'object';
    const valueText =
      typeof value === 'string'
        ? value
        : String((value as GeocodeValue | null)?.completo ?? '');
    const overlay = this.boundaryOverlay();
    const coords = this.currentCoordinates(value);
    const valueComposto =
      valueObj !== null ? String(valueObj['completo'] ?? '').trim() : '';
    const valueToponimo =
      valueObj !== null ? String(valueObj['toponimo'] ?? '').trim() : '';
    const valueCivico =
      valueObj !== null ? String(valueObj['civico'] ?? '').trim() : '';
    const valueCap = valueObj !== null ? String(valueObj['cap'] ?? '').trim() : '';
    const valuePlaceId =
      valueObj !== null ? String(valueObj['placeId'] ?? '').trim() : '';
    const valueHasPrediction =
      valueObj !== null &&
      !!valueObj['prediction'] &&
      typeof valueObj['prediction'] === 'object';
    return {
      valueType,
      valueText: valueText.length > 120 ? `${valueText.slice(0, 120)}…` : valueText,
      valueHasGeom:
        !!value && typeof value === 'object' && !!(value as GeocodeValue).geom_,
      valueCoords: coords ? `${coords[1]},${coords[0]}` : null,
      valueCompostoLength: valueComposto.length,
      valueToponimoLength: valueToponimo.length,
      valueCivicoLength: valueCivico.length,
      valueCapLength: valueCap.length,
      valueHasPlaceId: valuePlaceId.length > 0,
      valueHasPrediction,
      typedByUser: this.typedByUser(),
      selectedSuggestion: this.selectedSuggestion(),
      resolvingSelection: this.resolvingSelection(),
      queryEditing: this.queryEditing(),
      holdMapHidden: this.holdMapHiddenUntilBoundaryResolved(),
      selectionTransitionPending: this.selectionTransitionPending(),
      forcedMapOpen: this.forcedMapOpen(),
      keepMapVisible: this.keepMapVisible(),
      manualNoResult: this.manualNoResult(),
      mapDismissedByConfirm: this.mapDismissedByConfirm(),
      predictionsPending: this.predictionsPending(),
      boundaryCheckPending: this.boundaryCheckPending(),
      markerDragPending: this.markerDragPending(),
      overlayForceMap: overlay.forceMap,
      overlayPolygons: overlay.polygons.length,
      overlayMarkers: overlay.markers.length,
      showMapPreview: this.showMapPreview(),
    };
  }

  private debugLog(event: string, extra?: Record<string, unknown>) {
    if (!this.isDebugEnabled()) return;
    console.log('[geocode-debug]', {
      at: new Date().toISOString(),
      event,
      ...this.debugSnapshot(),
      ...(extra ?? {}),
    });
  }

  isMapToggleDisabled(): boolean {
    return !this.hasAddressForToggle();
  }

  onMapToggleClick() {
    this.toggleMapFromButton();
  }

  toggleMapExpanded() {
    const next = !this.mapExpanded();
    this.mapExpanded.set(next);
    this.setBodyScrollLocked(next);
  }

  private setBodyScrollLocked(locked: boolean) {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = locked ? 'hidden' : '';
  }
}
